import api from './axiosConfig';

export class FetchOrderConfirmationsAction {

    controller = {};
    responsesData = {};
    response = null;

    constructor() {
        this.response = {};
    }

    /**
     * Leegt de cache van opgehaalde orderbevestigingen om verse data te forceren bij volgende aanvragen
     */
    clearCache() {
        this.responsesData = {};
        this.controller = {};
    }

    /**
     * Haalt alle orderbevestigingen op met paginering, sortering en filtering
     * @param {number} page - Paginanummer
     * @param {number} pageSize - Aantal items per pagina
     * @param {string} sortField - Veld om op te sorteren
     * @param {string} sortDirection - Sorteervolgorde (ASC of DESC)
     * @param {string} search - Zoekterm
     * @param {string} status - Filter op status ID
     * @param {string} reference - Filter op referentienummer
     * @returns {Promise<Array>} - De opgevraagde orderbevestigingen
     */
    async getOrderConfirmations(
        page = 1,
        pageSize = 50,
        sortField = 'created_at',
        sortDirection = 'DESC',
        search = '',
        status = '',
        reference = '',
    ) {
        let requestKey = '/api/orderconfirmations?';
        requestKey += `page=${page}&limit=${pageSize}`;
        requestKey += `&sortDirection=${sortDirection}&sortField=${sortField}`;
        requestKey += `&search=${search}&status=${status}`;
        requestKey += `&reference=${reference}`;

        // Als we de data al in cache hebben, retourneer die meteen
        if (this.responsesData[requestKey]) {
            return this.responsesData[requestKey];
        }

        // Annuleer eerdere requests voor dezelfde URL
        if (this.controller[requestKey]) {
            this.controller[requestKey].abort();
        }
        this.controller[requestKey] = new AbortController();

        return this.#fetch(requestKey);
    }

    /**
     * Haalt een specifieke orderbevestiging op
     * @param {number} id - ID van de orderbevestiging
     * @returns {Promise<Object|null>} - De opgevraagde orderbevestiging of null
     */
    async getOrderConfirmationById(id) {
        const requestKey = `/api/orderconfirmations/${id}`;

        // Als we de data al in cache hebben, retourneer die meteen
        if (this.responsesData[requestKey]) {
            return this.responsesData[requestKey];
        }

        // Annuleer eerdere requests voor dezelfde URL
        if (this.controller[requestKey]) {
            this.controller[requestKey].abort();
        }
        this.controller[requestKey] = new AbortController();

        try {
            const response = await api.get(requestKey, {
                headers: { 'Content-Type': 'application/json' },
                signal: this.controller[requestKey].signal,
            });

            if (response.status === 401) {
                this.#dispatchCustomEvent('unauthorized', {});
                return null;
            }

            if (response.status !== 200 || !response.data) {
                console.error('Invalid response:', response);
                return null;
            }

            // Sla de response op in de cache
            this.responsesData[requestKey] = response.data.data;
            return response.data.data;
        } catch (error) {
            return this.#handleError(error, 'getOrderConfirmationFailed');
        }
    }

    /**
     * Haalt alle orderbevestigingsstatussen op
     * @returns {Promise<Array>} - De beschikbare statussen
     */
    async getOrderConfirmationStatuses() {
        const requestKey = '/api/orderconfirmations/statuses';

        // Als we de data al in cache hebben, retourneer die meteen
        if (this.responsesData[requestKey]) {
            return this.responsesData[requestKey];
        }

        // Annuleer eerdere requests voor dezelfde URL
        if (this.controller[requestKey]) {
            this.controller[requestKey].abort();
        }
        this.controller[requestKey] = new AbortController();

        try {
            const response = await api.get(requestKey, {
                headers: { 'Content-Type': 'application/json' },
                signal: this.controller[requestKey].signal,
            });

            if (response.status === 401) {
                this.#dispatchCustomEvent('unauthorized', {});
                return [];
            }

            if (response.status !== 200 || !response.data) {
                console.error('Invalid response:', response);
                return [];
            }

            // Sla de response op in de cache
            this.responsesData[requestKey] = response.data.data;
            return response.data.data;
        } catch (error) {
            return this.#handleError(error, 'getOrderConfirmationStatusesFailed');
        }
    }

    async #fetch(requestKey) {
        try {
            const response = await api.get(requestKey, {
                headers: { 'Content-Type': 'application/json' },
                signal: this.controller[requestKey].signal,
            });

            if (response.status === 401) {
                this.#dispatchCustomEvent('unauthorized', {});
                return [];
            }

            if (response.status !== 200 || !response.data) {
                console.error('Invalid response:', response);
                return [];
            }

            // Normaliseer de response data (we nemen aan dat de API data in een 'data' property retourneert)
            const normalizedData = response.data.data || [];
            this.responsesData[requestKey] = normalizedData;

            // Trigger een event na een korte vertraging om UI updates toe te staan
            this.#dispatchCustomEventWithDelay('orderConfirmationsLoaded', response.data);

            return normalizedData;
        } catch (error) {
            return this.#handleError(error, 'getOrderConfirmationsFailed');
        }
    }

    #dispatchCustomEventWithDelay(eventName, detail, delay = 100) {
        // Kleine timeout om de data te laten verwerken in de UI
        setTimeout(() => {
            this.#dispatchCustomEvent(eventName, detail);
        }, delay);
    }

    #dispatchCustomEvent(eventName, detail) {
        document.dispatchEvent(new CustomEvent(eventName, {
            detail,
            bubbles: true
        }));
    }

    #handleError(error, eventName) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return [];
        }

        if (error?.response?.status === 401) {
            this.#dispatchCustomEvent('unauthorized', {});
        } else {
            console.error('Error in response from server:', error.response?.data);
            this.#dispatchCustomEvent(eventName, error.response?.data || 'Error in database');
        }

        return [];
    }
}

export default new FetchOrderConfirmationsAction();
